/**
 * The rule the backend applies in `IOwnerCounter`, so the number shown here is the number the nightly scan and the
 * removal guard work with: every owner counts - members, guests, other service principals - except the EasyLife 365
 * Identity service principal, which is in the owners list so that we can manage the app and is nobody's responsibility.
 * It is recognised by its application id, which only a service principal carries.
 */
export type CountableOwnerCandidate = {
  id?: string | null;
  appId?: string | null;
};

export function isIdentityOwner(
  owner: CountableOwnerCandidate,
  identityAppId: string | undefined = import.meta.env.VITE_AUTH_CLIENTID
): boolean {
  return !!identityAppId && owner.appId?.toLowerCase() === identityAppId.toLowerCase();
}

export function isCountableOwner(
  owner: CountableOwnerCandidate,
  identityAppId: string | undefined = import.meta.env.VITE_AUTH_CLIENTID
): boolean {
  return !isIdentityOwner(owner, identityAppId);
}

/**
 * Graph stops an `$expand` of owners at this many items and says nothing about it: there is no next link and no
 * count. An expansion this full has to be read again through the owners collection, which pages.
 */
export const EXPANDED_OWNERS_LIMIT = 20;

type OwnersPage<TOwner> = {
  value?: TOwner[];
  '@odata.nextLink'?: string;
};

/** Returns every owner of an app: the expanded ones, or all pages of the owners collection when the expansion may have been cut off. */
export async function completeOwners<TOwner>(
  expanded: TOwner[] | undefined,
  getPage: (nextLink: string | undefined) => Promise<OwnersPage<TOwner>>
): Promise<TOwner[]> {
  if (!expanded || expanded.length < EXPANDED_OWNERS_LIMIT) return expanded ?? [];

  const owners: TOwner[] = [];
  let next: string | undefined;

  do {
    const page = await getPage(next);
    owners.push(...(page.value ?? []));
    next = page['@odata.nextLink'];
  } while (next);

  return owners;
}
